import { V2 } from "../theme/DesignV2";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Box, HBox } from "../box/Box";
import Button from "../button/Button";
import { IconArrowRight } from "../icons/IconArrowRight";
import { IconClose } from "../icons/IconClose";
import Spacer from "../spacer/Spacer";
import { Spinner } from "../spinner/Spinner";
import { Typo, TypoSmall } from "../text/Typo";
import { RecursiveCSS } from "../theme/GlobalTheme";
import { useWindowSize } from "../utils/useResize";
import { isMobileViewport } from "../utils/mobileViewport";
import { MOBILE_SHEET_BOTTOM_PADDING, MOBILE_SHEET_HEIGHT } from "./panelViewport";

// Спасибо Вова что ты запорол и этот компонент
interface IPanelProps {
	children?: ReactNode;
	scrollable?: boolean;
	decorator?: "absolute";
	busy?: boolean;
	label?: string;
	sx?: RecursiveCSS;
	onBack?: () => void;
	onClose?: () => void;
	contentPadding?: string;
	disableScroll?: boolean;
	growContent?: boolean;
	desktopPlacement?: "center" | "side";
	desktopWidth?: string;
}

const Panel = ({ children, onBack, onClose, label, scrollable, decorator, busy, sx, contentPadding, disableScroll, growContent, desktopPlacement = "center", desktopWidth = "600px" }: IPanelProps) => {
	const panelId = useId();
	const pointerStartedOnBackdrop = useRef<boolean | null>(null);
	//const theme = useTheme() as ITheme;

	const { width, height } = useWindowSize();
	const isMobile = isMobileViewport(width, height);
	const isMobileSheet = isMobile && decorator === "absolute";
	const isDesktopDrawer = !isMobile && decorator === "absolute" && desktopPlacement === "side";

	const [sheetVisible, setSheetVisible] = useState(false);

	useEffect(() => {
		if (isMobileSheet) {
			// trigger enter animation on next frame
			requestAnimationFrame(() => setSheetVisible(true));
		} else {
			setSheetVisible(false);
		}
	}, [isMobileSheet]);

	const handleClose = useCallback(() => {
		if (isMobileSheet) {
			setSheetVisible(false);
			setTimeout(() => {
				onClose?.();
				// A nested dialog can return to its parent without remounting Panel.
				// Restore the sheet so the transparent fullscreen overlay does not
				// remain above the page and swallow subsequent scroll gestures.
				setSheetVisible(true);
			}, 300);
		} else {
			onClose?.();
		}
	}, [isMobileSheet, onClose]);

	useEffect(() => {
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				const dialogs = document.querySelectorAll('[data-panel-id]');
				if (decorator === "absolute" && dialogs.length && dialogs[dialogs.length - 1].getAttribute('data-panel-id') !== panelId) return;
				handleClose();
			}
		};

		document.addEventListener("keydown", handleEscape);
		return () => document.removeEventListener("keydown", handleEscape);
	}, [decorator, handleClose, panelId]);

	useEffect(() => {
		if (decorator !== "absolute") return;
		const rememberPointerOrigin = (event: PointerEvent) => {
			pointerStartedOnBackdrop.current = event.target instanceof Element &&
				event.target.getAttribute("data-dialog-overlay-id") === panelId;
		};
		document.addEventListener("pointerdown", rememberPointerOrigin, true);
		return () => document.removeEventListener("pointerdown", rememberPointerOrigin, true);
	}, [decorator, panelId]);

	const handleBackdropClick = useCallback((e?: MouseEvent) => {
		if (e?.target === e?.currentTarget && pointerStartedOnBackdrop.current !== false) handleClose();
		pointerStartedOnBackdrop.current = null;
	}, [handleClose]);

	const hasContentWrapper = Boolean(label) || (decorator === "absolute" && Boolean(scrollable));
	const childrenContent = hasContentWrapper ? (
		<Box
			sx={{
				padding: contentPadding ?? (isMobileSheet ? "20px 16px" : label && decorator === "absolute" ? V2.spacing : label ? V2.spacing : "24px"),
				boxSizing: "border-box",
				minHeight: 0,
				overflowX: "hidden",
				overflowY: disableScroll ? "hidden" : "auto",
				overscrollBehavior: "contain",
				WebkitOverflowScrolling: "touch",
				flex: growContent ? "1 1 auto" : "1 1 0",
				display: "flex",
				flexDirection: "column",
			}}>
			{children}
		</Box>
	) : (
		children
	);

	const mobileSheetSx: RecursiveCSS = isMobileSheet
		? {
				width: "100%",
				minWidth: "unset",
				maxWidth: "100%",
				height: "auto",
				maxHeight: MOBILE_SHEET_HEIGHT,
				minHeight: "unset",
				boxSizing: "border-box",
				paddingBottom: MOBILE_SHEET_BOTTOM_PADDING,
				borderRadius: "16px 16px 0 0",
			}
		: {};

	const content = (
		<Box
			data-dialog-surface={decorator === "absolute" ? (isMobileSheet ? "mobile" : "desktop") : undefined}
			data-panel-id={decorator === "absolute" ? panelId : undefined}
			data-dialog-layout-mode={isDesktopDrawer ? "side" : undefined}
			role={decorator === "absolute" ? "dialog" : undefined}
			aria-modal={decorator === "absolute" ? true : undefined}
			aria-label={decorator === "absolute" ? label || "Dialog" : undefined}
			sx={{
				border: decorator === "absolute" ? "1px solid #E5E7EB" : "1px solid rgba(255, 255, 255, 0.05)",
				paddingTop: !hasContentWrapper ? (isMobileSheet ? "16px" : "30px") : undefined,
				paddingRight: !hasContentWrapper ? (isMobileSheet ? "16px" : "30px") : undefined,
				paddingBottom: !hasContentWrapper ? (isMobileSheet ? "16px" : "30px") : undefined,
				paddingLeft: !hasContentWrapper ? (isMobileSheet ? "16px" : "30px") : undefined,
				borderRadius: V2.dialogRadius,
				boxShadow: decorator === "absolute" ? "0 28px 80px rgba(15, 23, 42, 0.24), 0 8px 24px rgba(15, 23, 42, 0.10)" : "0px 0px 100px rgba(0,0,0,.1)",
				pointerEvents: busy ? "none" : "auto",
				userSelect: busy ? "none" : "auto",
				display: "flex",
				position: "relative",
				flexDirection: "column",
				backgroundColor: decorator === "absolute" ? "#FFFFFF" : "panelBackgroundPrimary",
				maxHeight: "95vh",
				overflow: decorator === "absolute" ? "hidden" : undefined,

				...sx,
				...(isDesktopDrawer ? { width: `min(${desktopWidth}, calc(100vw - 80px))`, maxWidth: desktopWidth, minWidth: 0, height: "100dvh", maxHeight: "100dvh", borderRadius: 0 } : {}),
				...mobileSheetSx,
			}}>
			{busy && (
				<Box
					sx={{
						position: "absolute",
						top: "0px",
						left: "0px",
						width: "100%",
						height: "100%",
						backgroundColor: "rgba(255,255,255,.5)",
						zIndex: 100000,
						display: "flex",
						flexDirection: "column",
						gap: "10px",
						justifyContent: "center",
						alignItems: "center",
					}}>
					<Spinner color="#ad1bf1" />
					<TypoSmall>Please wait...</TypoSmall>
				</Box>
			)}
			{isMobileSheet && (
				<Box sx={{ display: "flex", justifyContent: "center", paddingTop: "6px", paddingBottom: "2px", flexShrink: 0 }}>
					<Box sx={{ width: "36px", height: "4px", borderRadius: "2px", backgroundColor: "rgba(0,0,0,0.2)" }} />
				</Box>
			)}
			{label && (
				<>
					<HBox
						sx={{
							backgroundColor: decorator === "absolute" ? "#FFFFFF" : undefined,
							padding: isMobileSheet ? "8px 16px" : decorator === "absolute" ? "12px 16px" : "16px",
							boxSizing: "border-box",
							width: "100%",
							alignItems: "center",
							flexShrink: 0,
							zIndex: 1,
						}}>
						{onBack && (
							<Button
								variant="icon"
								icon={
									<IconArrowRight
										width="8px"
										height="12px"
										sx={{ transform: "rotate(180deg)" }}
									/>
								}
								ariaLabel="Back to previous dialog"
								sx={{
									width: "40px",
									height: "40px",
									minWidth: "40px",
									padding: 0,
									marginRight: "10px",
									flexShrink: 0,
									justifyContent: "center",
									borderRadius: "10px",
									color: "#6B7280",
									"&:hover": { backgroundColor: "#E5EAF2" },
								}}
								onClick={onBack}
							/>
						)}
						<Typo
							variant="head"
							sx={{
								display: "flex",
								paddingRight: "30px",
								overflowWrap: "break-word",
								wordBreak: "normal",
								width: "100%",
								fontWeight: decorator === "absolute" ? "700" : isMobileSheet ? "600" : "500",
								fontSize: isMobileSheet ? "1rem" : decorator === "absolute" ? "1rem" : "1rem",
									color: decorator === "absolute" ? "#111827" : undefined,
									letterSpacing: 0,
									lineHeight: 1.25,
								}}>
							{label}
						</Typo>
						{onClose && (
							<>
								<Spacer grow />
								<Button
									variant="icon"
									icon={<IconClose />}
									ariaLabel="Close dialog"
									sx={{
										width: "40px",
										height: "40px",
										minWidth: "40px",
										padding: 0,
										flexShrink: 0,
										justifyContent: "center",
										borderRadius: "10px",
										color: "#6B7280",
										"&:hover": { backgroundColor: "#E5EAF2" },
									}}
									onClick={handleClose}
								/>
							</>
						)}
					</HBox>
					<Box
						sx={{
							boxSizing: "border-box",
							background: decorator === "absolute" ? "#E5EAF2" : "panelDividerBackgroundColor",
							height: "1px",
							maxHeight: "1px",
							flexShrink: 0,
						}}
					/>
				</>
			)}
			{childrenContent}
			{busy && (
				<Box
					sx={{
						position: "absolute",
						top: "0px",
						left: "0px",
						width: "100%",
						height: "100%",
						backdropFilter: "saturate(0.2)",
						zIndex: 100001,
					}}
				/>
			)}
		</Box>
	);

	if (decorator !== "absolute") return content;

	// Keep the same content ancestry when the viewport crosses the sheet
	// breakpoint, so forms retain their state instead of mounting again.
	return (
		<Box
			onClick={handleBackdropClick}
			data-dialog-overlay-id={panelId}
			data-dialog-backdrop={isMobileSheet ? "mobile" : "desktop"}
			sx={{
				position: "fixed",
				inset: "0",
				width: "100%",
				height: isMobileSheet ? "100dvh" : "100%",
				minHeight: isMobileSheet ? "100dvh" : undefined,
				display: "flex",
				flexDirection: "column",
				justifyContent: isMobileSheet ? "flex-end" : "center",
				alignItems: isMobileSheet ? undefined : isDesktopDrawer ? "flex-end" : "center",
				overflow: isMobileSheet ? "hidden" : undefined,
				overscrollBehavior: isMobileSheet ? "contain" : undefined,
				zIndex: 1000,
				backgroundColor: isMobileSheet
					? sheetVisible ? "rgba(0,0,0,.5)" : "rgba(0,0,0,0)"
					: "rgba(20, 29, 49, .48)",
				backdropFilter: isMobileSheet ? undefined : "blur(6px)",
				transition: isMobileSheet ? "background-color 0.3s ease" : undefined,
			}}>
			<Box
				onClick={(e?: MouseEvent) => e?.stopPropagation()}
				sx={{
					display: isMobileSheet ? "block" : "contents",
					transform: isMobileSheet ? sheetVisible ? "translateY(0)" : "translateY(100%)" : undefined,
					transition: isMobileSheet ? "transform 0.3s ease" : undefined,
				}}>
				{content}
			</Box>
		</Box>
	);
};

export default Panel;
